import { CONSTANTS } from "client/utils/constants";

type OptionLike = {
    value?: any;
    label?: string;
    text?: string;
    showVehicleCapacity?: boolean;
};

type CriteriaState = {
    upfitOption?: OptionLike | null;
    snowSetup?: OptionLike | null;
    vehicle?: OptionLike | null;
    vehicleCapacity?: OptionLike | null;
    noOfDoors?: OptionLike | null;
    driveTrain?: OptionLike | null;
    fuelType?: OptionLike | null;
    driverTitle?: OptionLike | null;
    equipmentType?: OptionLike | null;
    managedEquipFields?: Record<string, OptionLike | null | undefined>;
};

type GateArgs = {
    effectiveSubType?: string | null;
    criteria?: CriteriaState | null;
};

type NormalizedCriteria = {
    upfitOption?: number | null;
    snowSetup?: number | null;
    vehicle?: number | null;
    vehicleCapacity?: number | null;
    noOfDoors?: number | null;
    driveTrain?: number | null;
    fuelType?: number | null;
    driverTitle?: number | null;
    equipmentType?: number | null;
    managedEquipFields?: Record<string, number | null>;
};

const OPTIONAL_SELECT_PREFIX = "select";

const toTrimmedText = (value: any): string => String(value ?? "").trim();

const hasSelectableLabel = (option: OptionLike | null | undefined): boolean => {
    const label = toTrimmedText(option?.label ?? option?.text);
    return label.length > 0 && !label.toLowerCase().startsWith(OPTIONAL_SELECT_PREFIX);
};

const normalizeOptionValue = (option: OptionLike | null | undefined): number | null => {
    if (!option) return null;
    const value = option.value;
    if (value === null || value === undefined || value === "") {
        return hasSelectableLabel(option) ? null : null;
    }

    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : null;
};

const normalizeManagedEquipFields = (fields?: Record<string, OptionLike | null | undefined>): Record<string, number | null> => {
    const normalized: Record<string, number | null> = {};
    if (!fields) return normalized;

    Object.keys(fields).forEach((key) => {
        normalized[key] = normalizeOptionValue(fields[key]);
    });

    return normalized;
};

const getManagedEquipmentParticipatingFields = (equipmentTypeLabel?: string | null): string[] => {
    switch (equipmentTypeLabel) {
        case CONSTANTS.MOWER:
            return ["manufacturer", "category", "deckSize", "deckType", "stateAvailability", "programStatus", "annualHours", "autonomous"];
        case CONSTANTS.SNOW:
            return ["manufacturer", "category", "size", "type"];
        case CONSTANTS.TRAILER:
            return ["manufacturer", "style", "length", "fuelTank", "chargingStation"];
        case CONSTANTS.UTILITY_VEHICLE:
            return ["manufacturer", "seatingCapacity", "enclosedCab", "capacity"];
        case CONSTANTS.CONSTRUCTION_EQUIPMENT:
            return ["manufacturer", "category"];
        case CONSTANTS.STANDARIZED_EQUIPMENT:
            return ["manufacturer", "category"];
        default:
            return [];
    }
};

export const normalizeCatalogOverrideCriteria = ({ effectiveSubType, criteria }: GateArgs): NormalizedCriteria => {
    const normalized: NormalizedCriteria = {};
    const state = criteria ?? {};

    if (effectiveSubType === CONSTANTS.PRODUCTION_VEHICLE) {
        const vehicleRequiresCapacity = !!state.vehicle?.showVehicleCapacity;
        normalized.upfitOption = normalizeOptionValue(state.upfitOption);
        normalized.snowSetup = normalizeOptionValue(state.snowSetup);
        normalized.vehicle = normalizeOptionValue(state.vehicle);
        normalized.vehicleCapacity = vehicleRequiresCapacity ? normalizeOptionValue(state.vehicleCapacity) : null;
        normalized.noOfDoors = normalizeOptionValue(state.noOfDoors);
        normalized.driveTrain = normalizeOptionValue(state.driveTrain);
        normalized.fuelType = normalizeOptionValue(state.fuelType);
        return normalized;
    }

    if (effectiveSubType === CONSTANTS.MANAGEMENT_VEHICLE) {
        normalized.driverTitle = normalizeOptionValue(state.driverTitle);
        normalized.vehicle = normalizeOptionValue(state.vehicle);
        normalized.driveTrain = normalizeOptionValue(state.driveTrain);
        normalized.fuelType = normalizeOptionValue(state.fuelType);
        return normalized;
    }

    normalized.equipmentType = normalizeOptionValue(state.equipmentType);
    const equipmentTypeLabel = toTrimmedText(state.equipmentType?.label ?? state.equipmentType?.text);
    const participatingFields = getManagedEquipmentParticipatingFields(equipmentTypeLabel);
    const fieldValues = normalizeManagedEquipFields(state.managedEquipFields);
    normalized.managedEquipFields = {};
    participatingFields.forEach((field) => {
        normalized.managedEquipFields![field] = fieldValues[field] ?? null;
    });
    return normalized;
};

export const isCatalogOverrideBaselineReady = ({ effectiveSubType, criteria }: GateArgs): boolean => {
    const state = criteria ?? {};

    if (effectiveSubType === CONSTANTS.PRODUCTION_VEHICLE) {
        return normalizeOptionValue(state.upfitOption) !== null
            && normalizeOptionValue(state.snowSetup) !== null
            && normalizeOptionValue(state.vehicle) !== null
            && normalizeOptionValue(state.noOfDoors) !== null
            && normalizeOptionValue(state.driveTrain) !== null
            && (!state.vehicle?.showVehicleCapacity || normalizeOptionValue(state.vehicleCapacity) !== null);
    }

    if (effectiveSubType === CONSTANTS.MANAGEMENT_VEHICLE) {
        return normalizeOptionValue(state.driverTitle) !== null
            && normalizeOptionValue(state.vehicle) !== null
            && normalizeOptionValue(state.driveTrain) !== null;
    }

    return normalizeOptionValue(state.equipmentType) !== null;
};

export const hasMaterialCatalogOverrideChange = (
    originalCriteria?: NormalizedCriteria | null,
    currentCriteria?: NormalizedCriteria | null,
): boolean => {
    if (!originalCriteria || !currentCriteria) return false;

    const compareScalar = (key: keyof NormalizedCriteria): boolean => {
        const originalValue = originalCriteria[key] as number | null | undefined;
        const currentValue = currentCriteria[key] as number | null | undefined;
        return originalValue != null && originalValue !== currentValue;
    };

    if (compareScalar("upfitOption")
        || compareScalar("snowSetup")
        || compareScalar("vehicle")
        || compareScalar("vehicleCapacity")
        || compareScalar("noOfDoors")
        || compareScalar("driveTrain")
        || compareScalar("fuelType")
        || compareScalar("driverTitle")
        || compareScalar("equipmentType")) {
        return true;
    }

    const originalManagedFields = originalCriteria.managedEquipFields ?? {};
    const currentManagedFields = currentCriteria.managedEquipFields ?? {};

    return Object.keys(originalManagedFields).some((field) => {
        const originalValue = originalManagedFields[field];
        return originalValue != null && originalValue !== (currentManagedFields[field] ?? null);
    });
};
